import { type FunctionComponent, useCallback } from 'react'
import { useIntl } from 'react-intl'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { useHotkeys, useOs } from '@mantine/hooks'
import { MenuBase, MenuItem, NavigationBar } from '@retailer-platform/shared-components/src/tds'
import { ICPlatformVersion } from '../../../utils/ic-platform/versioning/versions.constants'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { useTopBarMenu } from './TopBarMenuContext'

const SearchAccessControlConfig: AccessControlConfig = {
  featureToggles: ['rt_ipp_nav_search' as FeatureToggle],
  minICPlatformVersion: ICPlatformVersion.r2025_03_0,
}

export const TdsSearchButton: FunctionComponent = () => {
  const intl = useIntl()
  const { isOpen, setIsOpen } = useTopBarMenu('search')
  const hasAccess = useAccessControl()
  const trackEvent = useTrackEvent()
  const os = useOs()

  const openSearch = useCallback(() => setIsOpen(true), [setIsOpen])

  useHotkeys([
    ['mod+k', openSearch],
    ['ctrl+k', openSearch],
  ])

  const onMenuStateChange = useCallback(
    (next: boolean) => {
      setIsOpen(next)
      if (next) {
        trackEvent({
          id: 'navV2.searchSpotlight.open',
          description: 'User is opening the search bar',
        })
      } else {
        trackEvent({
          id: 'navV2.searchSpotlight.close',
          description: 'User is closing the search bar',
        })
      }
    },
    [setIsOpen, trackEvent]
  )

  if (!hasAccess(SearchAccessControlConfig)) {
    return null
  }

  const tooltipHotkey = os === 'macos' || os === 'ios' ? '⌘K' : 'Ctrl+K'

  const trigger = (
    <NavigationBar.Button
      aria-label={`${intl.formatMessage({ id: 'app.nav.searchMenu.label' })} (${tooltipHotkey})`}
      icon="search"
      onPress={() => onMenuStateChange(!isOpen)}
    />
  )

  return (
    <MenuBase
      target={trigger}
      isOpen={isOpen}
      onMenuStateChange={onMenuStateChange}
      position="bottomEnd"
    >
      <MenuItem text={intl.formatMessage({ id: 'app.nav.searchMenu.placeholder' })} disabled />
    </MenuBase>
  )
}
